import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFilterStore } from '../page/search/searchFilterPanel/FilterStoreProvider';
import useSortStore from '../page/search/searchSort/SortStore';
import { FILTER_KEYS } from '../constants/constants';

/**
 * 필터/정렬 URL <-> 상태 동기화 공용 훅
 * - URL 쿼리를 읽어 Zustand 스토어 초기화
 * - 스토어 변경 시 URL 쿼리로 반영 (replace)
 */
export default function useFilterSortUrlSync(defaultSort = 'POPULAR') {
  const filters = useFilterStore((state) => state.filters);
  const initializeFilters = useFilterStore((state) => state.initializeFilters);
  const selectedSort = useSortStore((state) => state.selectedSort);
  const initializeSort = useSortStore((state) => state.initializeSort);

  const location = useLocation();
  const navigate = useNavigate();

  // 내부 navigate로 인한 URL 변경 시 Effect 1 재실행을 방지하기 위한 ref
  const isInternalNavigation = useRef(false);
  // defaultSort를 ref로 관리해 Effect 1의 dependency에서 제외
  const defaultSortRef = useRef(defaultSort);
  defaultSortRef.current = defaultSort;

  // URL -> 상태 초기화
  useEffect(() => {
    if (isInternalNavigation.current) {
      isInternalNavigation.current = false;
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    const initialFilters = {
      [FILTER_KEYS.GENDER]: searchParams.get(FILTER_KEYS.GENDER) || '',
      categories: searchParams.getAll('category') || [],
      [FILTER_KEYS.SUBCATEGORY_IDS]: (() => {
        const idsParam = searchParams.get(FILTER_KEYS.SUBCATEGORY_IDS);
        if (!idsParam) return [];
        return idsParam
          .split(',')
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
      })(),
      [FILTER_KEYS.BRANDS]: (() => {
        const bParam = searchParams.get(FILTER_KEYS.BRANDS);
        if (bParam) return bParam.split(',');
        return searchParams.getAll('brand') || [];
      })(),
      [FILTER_KEYS.PRICE_MIN]: searchParams.get(FILTER_KEYS.PRICE_MIN)
        ? parseInt(searchParams.get(FILTER_KEYS.PRICE_MIN), 10)
        : null,
      [FILTER_KEYS.PRICE_MAX]: searchParams.get(FILTER_KEYS.PRICE_MAX)
        ? parseInt(searchParams.get(FILTER_KEYS.PRICE_MAX), 10)
        : null,
      [FILTER_KEYS.IS_SELLING]: searchParams.get(FILTER_KEYS.IS_SELLING) === 'true',
      [FILTER_KEYS.CONDITION]: searchParams.get(FILTER_KEYS.CONDITION) || '',
    };

    initializeFilters(initialFilters);

    const sortParam = searchParams.get(FILTER_KEYS.SORT);
    if (sortParam) {
      initializeSort(sortParam);
    } else {
      // URL에 정렬 파라미터가 없으면 페이지별 기본값으로 초기화
      initializeSort(defaultSortRef.current);
    }
  }, [location.search, initializeFilters, initializeSort]);

  // 상태 -> URL 동기화
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }

    const params = new URLSearchParams();
    if (filters[FILTER_KEYS.GENDER]) params.set(FILTER_KEYS.GENDER, filters[FILTER_KEYS.GENDER]);

    if (
      filters[FILTER_KEYS.SUBCATEGORY_IDS] &&
      filters[FILTER_KEYS.SUBCATEGORY_IDS].length
    ) {
      params.set(
        FILTER_KEYS.SUBCATEGORY_IDS,
        filters[FILTER_KEYS.SUBCATEGORY_IDS].join(',')
      );
    }

    if (filters[FILTER_KEYS.BRANDS] && filters[FILTER_KEYS.BRANDS].length) {
      const names = filters[FILTER_KEYS.BRANDS].map((b) =>
        typeof b === 'string' ? b : b.name
      );
      params.set(FILTER_KEYS.BRANDS, names.join(','));
    }

    if (filters[FILTER_KEYS.PRICE_MIN] != null)
      params.set(FILTER_KEYS.PRICE_MIN, filters[FILTER_KEYS.PRICE_MIN]);
    if (filters[FILTER_KEYS.PRICE_MAX] != null)
      params.set(FILTER_KEYS.PRICE_MAX, filters[FILTER_KEYS.PRICE_MAX]);

    if (filters[FILTER_KEYS.IS_SELLING]) params.set(FILTER_KEYS.IS_SELLING, 'true');
    if (filters[FILTER_KEYS.CONDITION])
      params.set(FILTER_KEYS.CONDITION, filters[FILTER_KEYS.CONDITION]);
    if (selectedSort) params.set(FILTER_KEYS.SORT, selectedSort);

    const newQuery = params.toString();
    const currentQuery = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;

    if (newQuery === currentQuery) return;

    isInternalNavigation.current = true;
    navigate(`${location.pathname}${newQuery ? `?${newQuery}` : ''}`, {
      replace: true,
    });
  }, [filters, selectedSort, navigate, location.pathname, isInitialized]);
}
